import { useEffect } from 'react';
import {
  DownloadMediasEventsEnum,
  useDownloadMediasNotifier,
} from '../../../../hooks/useDownloadMediasNotifier';
import LangEnum from '../../../../lang/LangEnum';
import downloadMediasNotifierStore, {
  DownloadMediasNotifierOrder,
  useDownloadMediasNotifierStore,
} from '../../../../stores/DownloadMediasNotifier.store';
import snackbarStore from '../../../../stores/Snackbar.store';
import { useTranslation } from '../../../translation/TranslationContext';
import { TranslateType } from '../../../translation/translationContext.types';
import { downloadMediasFrame } from '../downloadMediasFrame';

type MediasProcessedSocket = { orderId: string; url: string; error: { message: string } };

const PARTIAL_ERROR_NOTICE = {
  type: 'info',
  label: LangEnum.SNACKBAR_PARTIAL_ERROR_DOWNLOADING_MEDIA,
} as const;
const ERROR_NOTICE = { type: 'error', label: LangEnum.SNACKBAR_ERROR_DOWNLOADING_MEDIA } as const;

const getDownloadMediasSnackbarId = (orderId: string) => `download-medias-${orderId}`;

const handleMediasProcessed = (
  translate: TranslateType,
  { orderId, url, error }: MediasProcessedSocket,
) => {
  const removedOrder = downloadMediasNotifierStore.removeOrder(orderId);

  if (error) {
    const { type, label } = url ? PARTIAL_ERROR_NOTICE : ERROR_NOTICE;
    snackbarStore.handleMessage({
      type,
      message: translate(label, { errorMessage: error.message }),
    });
  }

  if (removedOrder) {
    downloadMediasFrame(url);
  }
};

export const useDownloadMediasSnackbar = () => {
  const { translate } = useTranslation();
  const socket = useDownloadMediasNotifier();

  useEffect(() => {
    const showDownloading = ({ id, code }: DownloadMediasNotifierOrder) =>
      snackbarStore.handleMessage({
        id: getDownloadMediasSnackbarId(id),
        type: 'info',
        loading: true,
        message: translate(LangEnum.ORDER_DOWNLOAD_MEDIAS_MESSAGE, { order: code }),
      });

    downloadMediasNotifierStore.ordersDownloadingMedias.forEach(showDownloading);

    const unsubscribe = useDownloadMediasNotifierStore.subscribe((state, previous) => {
      const currentIds = new Set(state.ordersDownloadingMedias.map(({ id }) => id));
      const previousIds = new Set(previous.ordersDownloadingMedias.map(({ id }) => id));

      state.ordersDownloadingMedias
        .filter(({ id }) => !previousIds.has(id))
        .forEach(showDownloading);

      previous.ordersDownloadingMedias
        .filter(({ id }) => !currentIds.has(id))
        .forEach(({ id }) => snackbarStore.close(getDownloadMediasSnackbarId(id)));
    });

    return () => {
      unsubscribe();
      downloadMediasNotifierStore.ordersDownloadingMedias.forEach(({ id }) =>
        snackbarStore.close(getDownloadMediasSnackbarId(id)),
      );
    };
  }, [translate]);

  useEffect(() => {
    if (!socket) {
      return;
    }

    socket.on(DownloadMediasEventsEnum.MEDIAS_PROCESSED, (payload: MediasProcessedSocket) =>
      handleMediasProcessed(translate, payload),
    );

    return () => {
      socket.off(DownloadMediasEventsEnum.MEDIAS_PROCESSED);
    };
  }, [socket, translate]);
};
